import React from 'react'
import styled from 'styled-components'
import emojiRegex from 'emoji-regex'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { getTwEmoji } from 'utils/emojiUtil'
import messageUtil from 'utils/messageUtil'
import { getInsightContentTypeDisplayText } from 'components/insightContent/utils/utils'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
// @ts-ignore
import { Tooltip, Dialog } from '@netbasequid/core'
import AccordionSectionType from 'components/insightContent/enum/AccordionSectionType'
import {
  TopInfoBox,
  TopInfoRight,
  StyledTermContainer,
  StyledTermContent
} from 'components/insightContent/visualization/InfoBox/index.css'
import { CountTypeStrings } from 'enums/CountType'
import InsightViewOption from 'enums/InsightViewOption'
import { InsightContentSettingType } from 'components/insightContent/types'
import AccordionGroup from 'components/insightContent/visualization/AccordionGroup'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import { MetricTimeUnitStrings } from 'enums/MetricTimeUnit'
import { sendTypedMessage } from 'utils/iframeUtil'
import { toExternal } from 'vo/util'
import GoToAnalysisButton from 'components/insightContent/visualization/GoToAnalysisButton'
import InsightDialog from 'components/insightContent/visualization/InsightDialog'
import { InsightObjective } from '../../types'

const DIALOG_CONTENT_HEIGHT = '460px'

const Name = styled.span`
  font-weight: ${sys.fontWeight.semibold};
`

const resolveFormattedName = (insightObjective: InsightObjective) =>
  emojiRegex().test(insightObjective.name)
    ? getTwEmoji(insightObjective.name)
    : insightObjective.name

const resolveTooltip = (insightObjective: InsightObjective) => {
  const formattedName = resolveFormattedName(insightObjective)
  if (insightObjective.insight.variants && insightObjective.insight.variants.length > 0) {
    return (
      <div>
        {formattedName}
        {`, ${insightObjective.insight.variants.join(', ')}`}
      </div>
    )
  }

  return formattedName
}

const InsightModal = ({
  open,
  title,
  countType,
  selection,
  onClose,
  detailSetting,
  timeUnits,
  forceNotScale,
  widgetSettingWithSelection
}: {
  open: boolean
  title: string | undefined
  countType: CountTypeStrings
  selection: InsightObjective[] | undefined
  onClose: () => void
  detailSetting: InsightContentSettingType | undefined
  timeUnits?: MetricTimeUnitStrings[]
  forceNotScale?: boolean
  widgetSettingWithSelection?: any
}) => {
  const theme = useDisplayTheme() as Theme

  function goToAnalysis() {
    sendTypedMessage('goToAnalysis', toExternal(widgetSettingWithSelection))
  }

  const renderNameMeasureTitleContent = () => (
    <>
      <Name>{resolveFormattedName(selection![0])}</Name>&nbsp;
      {messageUtil.countType(countType)}
    </>
  )
  const accordionSummaryContents = []
  accordionSummaryContents.push({
    insightContentType: InsightContentType.TOTAL_MEASURE,
    detailSetting
  })

  switch (detailSetting?.viewOption) {
    case InsightViewOption.ATTRIBUTES:
    case InsightViewOption.BEHAVIORS:
    case InsightViewOption.EMOTIONS:
      accordionSummaryContents.push({
        insightContentType: InsightContentType.MEASURE_WITH_INSIGHTS,
        detailSetting
      })
      accordionSummaryContents.push({
        insightContentType: InsightContentType.NAME_MEASURE_WITH_SHARE_OF_INSIGHTS,
        detailSetting,
        renderNameMeasureTitleContent
      })
      break

    default:
      accordionSummaryContents.push({
        insightContentType: InsightContentType.NAME_MEASURE,
        detailSetting,
        renderNameMeasureTitleContent
      })
  }

  return (
    <InsightDialog open={open} onClose={onClose}>
      <Dialog.DialogTitle>{title}</Dialog.DialogTitle>
      <TopInfoBox mode={theme}>
        {selection?.map((insightObjective: InsightObjective, index: number) => (
          <StyledTermContainer
            // eslint-disable-next-line react/no-array-index-key
            key={`${insightObjective.name}-${index}`}>
            <Tooltip mode={theme} enabled content={resolveTooltip(insightObjective)}>
              <StyledTermContent
                // eslint-disable-next-line react/no-array-index-key
                key={`${insightObjective.name}-${index}`}
                mode={theme}>
                {resolveFormattedName(insightObjective)}
              </StyledTermContent>
            </Tooltip>
          </StyledTermContainer>
        ))}
        <TopInfoRight>
          <GoToAnalysisButton onClick={() => goToAnalysis()} />
        </TopInfoRight>
      </TopInfoBox>
      <Dialog.DialogContent height={DIALOG_CONTENT_HEIGHT}>
        <AccordionGroup
          timeUnits={timeUnits}
          forceNotScale={forceNotScale}
          countType={countType}
          insightContentsConfig={[
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SUMMARY),
              accordionContents: accordionSummaryContents
            },
            {
              accordionTitle: getInsightContentTypeDisplayText(AccordionSectionType.SOUND_BITE),
              accordionContents: [
                {
                  insightContentType: InsightContentType.SOUND_BITE,
                  detailSetting
                }
              ]
            }
          ]}
        />
      </Dialog.DialogContent>
      <Dialog.DialogActions />
    </InsightDialog>
  )
}

export default InsightModal
